import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, TrendingUp, ExternalLink, Tag, Ruler, Palette, Grid3x3, DollarSign } from "lucide-react";

/**
 * AdminPurchasingIntelligence — the "buying scorecard" for the next thrift run.
 *
 * Layer 1: rolls up her own sold data (last 30/60/90/365 days) sliced by
 * brand, size, color, category, price tier. Each slice returns the top 10
 * ranked by net profit + secondary sort by units sold.
 *
 * Layer 2: each brand row includes a Google Trends deep-link — one-tap
 * validation of whether public search interest matches her internal signal.
 * This is a deep-link (not an API call) so it never depends on scraper
 * reliability or rate limits.
 */

const WINDOWS = [
  { days: 30, label: "30 days" },
  { days: 60, label: "60 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "past year" },
];

const CARD_META = [
  { key: "brands", title: "brands", icon: Tag, hint: "which labels sell fastest at the highest net" },
  { key: "sizes", title: "sizes", icon: Ruler, hint: "the size range that consistently moves" },
  { key: "colors", title: "colors", icon: Palette, hint: "palette that finds a buyer quickest" },
  { key: "categories", title: "categories", icon: Grid3x3, hint: "which garment types pay for themselves" },
  { key: "price_tiers", title: "buy price tier", icon: DollarSign, hint: "the buy-in bracket with the best ROI" },
];

export default function AdminPurchasingIntelligence() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      setData(null);
      try {
        const { data } = await api.get(`/analytics/purchasing-intelligence?days=${days}`);
        setData(data);
      } catch (e) {
        toast.error(`could not load intelligence: ${e.response?.data?.detail || e.message}`);
        setData({ total_sold_in_window: 0, brands: [], sizes: [], colors: [], categories: [], price_tiers: [] });
      }
    })();
  }, [days]);

  return (
    <AdminLayout>
      <div className="p-6 sm:p-10 space-y-6 max-w-7xl">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="editorial-label mb-1">field · sourcing intelligence</p>
            <h1 className="font-serif text-4xl text-charcoal">purchasing intelligence</h1>
            <p className="text-espresso mt-2 max-w-3xl text-sm">
              your buying scorecard for the next thrift run. every leaderboard below is your <em>own</em> sold data,
              ranked by net profit — proof of what actually pays off. tap any brand for a live google trends check.
            </p>
          </div>
          <div className="flex items-center gap-1 border border-taupe/50 bg-ivory">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                onClick={() => setDays(w.days)}
                data-testid={`pi-window-${w.days}`}
                className={`px-3 py-2 text-[0.65rem] tracking-widest uppercase transition-colors ${
                  days === w.days ? "bg-charcoal text-ivory" : "text-taupe hover:text-charcoal"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </header>

        {data === null && (
          <p className="text-taupe text-sm" data-testid="pi-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> aggregating…</p>
        )}

        {data && data.total_sold_in_window === 0 && (
          <div className="border border-taupe/40 p-8 text-center text-taupe text-sm" data-testid="pi-empty">
            no sales in the last {data.window_days} days yet. once you import a nifty orders csv or record a direct sale,
            the leaderboards fill in.
          </div>
        )}

        {data && data.total_sold_in_window > 0 && (
          <>
            <p className="editorial-label" data-testid="pi-window-total">
              {data.total_sold_in_window} sold piece{data.total_sold_in_window === 1 ? "" : "s"} in the last {data.window_days} days
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {CARD_META.map((m) => (
                <LeaderboardCard
                  key={m.key}
                  meta={m}
                  rows={data[m.key]}
                  showTrendsLink={m.key === "brands"}
                />
              ))}
            </div>

            <div className="border border-brass/60 bg-linen p-5 flex items-start gap-3" data-testid="pi-tips">
              <TrendingUp className="w-4 h-4 text-brass mt-0.5 flex-shrink-0" />
              <div className="text-xs text-espresso space-y-1">
                <p className="text-charcoal font-medium">how to read this</p>
                <p>• <strong>net profit</strong> is the leaderboard's sort key — a $200 piece bought at $2 outranks a $100 piece bought at $50 even at the same net.</p>
                <p>• <strong>avg days to sell</strong> under 30 = hot, 60+ = slow. combine with net to find your true winners.</p>
                <p>• buy price tier tells you where your best <em>ROI multipliers</em> live — often the $5-10 bucket beats the $40+ bucket on % return.</p>
                <p>• tap a brand's ↗ icon to open google trends in a new tab — validate your instinct against public search interest.</p>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function LeaderboardCard({ meta, rows, showTrendsLink }) {
  const Icon = meta.icon;
  return (
    <div className="border border-taupe/40 bg-ivory" data-testid={`pi-card-${meta.key}`}>
      <div className="border-b border-taupe/30 px-4 py-3 flex items-center gap-2">
        <Icon className="w-4 h-4 text-brass" />
        <div className="flex-1">
          <p className="text-charcoal font-medium">{meta.title}</p>
          <p className="text-[0.6rem] tracking-widest uppercase text-taupe">{meta.hint}</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="p-4 text-taupe text-xs italic">no data in this window.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-linen/60 text-[0.55rem] tracking-widest uppercase text-taupe">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">key</th>
              <th className="px-3 py-2 text-right">units</th>
              <th className="px-3 py-2 text-right">net</th>
              <th className="px-3 py-2 text-right">margin</th>
              <th className="px-3 py-2 text-right">avg days</th>
              {showTrendsLink && <th className="px-2 py-2 text-center" title="google trends"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${meta.key}-${r.key}-${i}`} className="border-t border-taupe/20" data-testid={`pi-row-${meta.key}-${i}`}>
                <td className="px-3 py-2 text-taupe">{i + 1}</td>
                <td className="px-3 py-2 text-charcoal font-medium">{r.key}</td>
                <td className="px-3 py-2 text-right">{r.units_sold}</td>
                <td className={`px-3 py-2 text-right font-medium ${r.net_profit >= 0 ? "text-emerald-800" : "text-red-800"}`}>
                  ${r.net_profit.toFixed(2)}
                </td>
                <td className={`px-3 py-2 text-right ${r.avg_margin_pct == null ? "text-taupe" : r.avg_margin_pct >= 100 ? "text-emerald-800 font-medium" : "text-charcoal"}`}>
                  {r.avg_margin_pct == null ? "—" : `${r.avg_margin_pct}%`}
                </td>
                <td className={`px-3 py-2 text-right ${r.avg_days_to_sell == null ? "text-taupe" : r.avg_days_to_sell <= 30 ? "text-emerald-800" : r.avg_days_to_sell >= 60 ? "text-amber-800" : "text-charcoal"}`}>
                  {r.avg_days_to_sell == null ? "—" : `${r.avg_days_to_sell}d`}
                </td>
                {showTrendsLink && (
                  <td className="px-2 py-2 text-center">
                    {r.key && r.key !== "(unbranded)" && (
                      <a
                        href={`https://trends.google.com/trends/explore?q=${encodeURIComponent(r.key)}&geo=US&date=today%2012-m`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brass hover:text-charcoal inline-block"
                        title={`google trends: ${r.key}`}
                        data-testid={`pi-trends-${i}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
